/**
 * Everything the workspace can show about one roster line beyond its price.
 *
 * The Project Details sheet carries a row per person with their handle, what
 * they were asked to deliver, the link to what they posted, and a comments
 * column. None of that was in the workspace, so staff were still opening the
 * sheet to answer "who is this and what did they do".
 *
 * Most of it is already in the browser: the workspace fetches this campaign's
 * invoices, and an invoice carries the creator's profiles and the posts matched
 * to them. So the line is joined to its invoice here rather than waiting on new
 * roster fields. Where the join finds nothing, the section is simply absent.
 *
 * Pure.
 */

const text = (value) => String(value ?? '').trim();

/** Lowercased, whitespace-collapsed — for comparing names and emails. */
function key(value) {
  return text(value).toLowerCase().replace(/\s+/g, ' ');
}

const PLATFORM_RANK = { instagram: 0, youtube: 1 };

/** Instagram first — it is the handle staff recognise. */
function byPlatform(a, b) {
  const rank = (p) => (PLATFORM_RANK[p] === undefined ? 9 : PLATFORM_RANK[p]);
  return rank(a.platform) - rank(b.platform);
}

function emailOf(invoice) {
  return key(invoice.influencer_email) || key(invoice.contact_email);
}

function timeOf(invoice) {
  const parsed = Date.parse(invoice.submitted_at || invoice.created_at || '');
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * The invoice that belongs to this line, or null.
 *
 * The roster row carries `invoice_id`, so the usual case is an exact match.
 * The fallbacks are for a line the API has not linked: the roster id, then the
 * influencer id, then email, then the name — which is all a vendor line has,
 * since nobody signs in for it. A rejected invoice is usually followed by a
 * corrected one, so a live invoice wins over a rejected one and the newest
 * wins among equals.
 */
export function matchInvoice(line, invoices) {
  const row = line || {};
  const list = Array.isArray(invoices) ? invoices : [];
  if (list.length === 0) return null;

  const name = key(row.influencer_name) || key(row.vendor_name);
  const email = key(row.email) || key(row.influencer_email);

  const score = (invoice) => {
    if (row.invoice_id && invoice.id === row.invoice_id) return 5;
    if (row.id && invoice.shortlisted_influencer_id === row.id) return 4;
    if (row.influencer_id && invoice.influencer_id === row.influencer_id) return 3;
    if (email && emailOf(invoice) === email) return 2;
    if (name && key(invoice.influencer_name) === name) return 1;
    return 0;
  };

  return list.reduce((best, invoice) => {
    const found = score(invoice);
    if (found === 0) return best;
    if (!best) return invoice;

    const bestScore = score(best);
    if (found !== bestScore) return found > bestScore ? invoice : best;

    const live = (each) => (each.status === 'rejected' ? 0 : 1);
    if (live(invoice) !== live(best)) return live(invoice) > live(best) ? invoice : best;
    return timeOf(invoice) > timeOf(best) ? invoice : best;
  }, null);
}

/** `[{platform, handle}]` for the line, from the line or its invoice. */
export function lineProfiles(line, invoice) {
  const row = line || {};
  const fromLine = Array.isArray(row.influencer_profiles) ? row.influencer_profiles : null;
  const fromInvoice =
    invoice && Array.isArray(invoice.influencer_profiles) ? invoice.influencer_profiles : null;
  const single = text(row.handle) ? [{ platform: row.platform || 'instagram', handle: row.handle }] : [];

  return (fromLine || fromInvoice || single)
    .filter((profile) => profile && text(profile.handle))
    .slice()
    .sort(byPlatform);
}

/**
 * The planner page for a creator. `/planner/profile-details` resolves the
 * handle itself, so this works for a creator the index has never seen.
 */
export function profileHref(profile) {
  if (!profile || !text(profile.handle)) return null;
  const platform = text(profile.platform) || 'instagram';
  return `/planner/profile-details/${platform}/${encodeURIComponent(
    text(profile.handle),
  )}?src=invoice_workspace`;
}

/**
 * Posts for this line, deduped by url.
 *
 * `fromCampaign` is the campaign's own feed, attributed to this line — the
 * primary source, since a post is live long before an invoice exists. The line
 * and its invoice are read too, for anything the creator submitted by hand.
 */
export function linePosts(line, invoice, fromCampaign) {
  const row = line || {};
  const lists = [fromCampaign, row.campaign_posts, invoice && invoice.campaign_posts];
  const seen = new Set();

  return lists.reduce((posts, list) => {
    (Array.isArray(list) ? list : []).forEach((post) => {
      const url = post && text(post.url);
      if (!url || seen.has(url)) return;
      seen.add(url);
      posts.push(post);
    });
    return posts;
  }, []);
}

/**
 * The line's comment trail: the staff note, plus every reason already recorded
 * against it elsewhere. Ordered by source rather than by time — a staff note is
 * what someone came to read, and a block is what stops the payment.
 */
export function lineComments(line, context) {
  const row = line || {};
  const { invoice, link } = context || {};
  const comments = [];

  const add = (id, label, value, meta = {}) => {
    if (!text(value)) return;
    comments.push({ id, label, text: text(value), ...meta });
  };

  add('note', 'Note', row.notes, { by: row.notes_by, at: row.notes_at });
  add('block', 'Blocked', row.block_reason);
  add('unlock', 'Price unlocked', row.price_unlock_reason, {
    by: row.price_unlocked_by,
    at: row.price_unlocked_at,
  });
  if (link) {
    add('exception', 'Exception link', link.reason, { by: link.created_by, at: link.created_at });
  }
  if (invoice) {
    const rejected = invoice.status === 'rejected';
    add('invoice', rejected ? 'Invoice rejected' : 'Invoice remark', invoice.remarks, {
      at: invoice.status_changed_at || invoice.submitted_at,
    });
  }

  return comments;
}

/**
 * Which of the fields just saved did not come back on the reloaded line.
 *
 * Several roster fields do not exist in the API yet. Without this a staff note
 * would report "saved" and then vanish on the next load; with it the row says
 * plainly that the field has not shipped.
 */
export function unpersisted(sent, row) {
  const payload = sent || {};
  const saved = row || {};
  return Object.keys(payload).filter((field) => {
    const was = text(payload[field]);
    const now = text(saved[field]);
    if (was === now) return false;
    // The form holds numbers as strings; 40000 and '40000' are the same figure.
    const asNumber = (value) => (value === '' ? null : Number(value));
    const a = asNumber(was);
    const b = asNumber(now);
    if (a !== null && b !== null && Number.isFinite(a) && Number.isFinite(b)) return a !== b;
    return true;
  });
}
